class PredictiveSearch extends SearchForm {
  /**
   * @type {Map<any, any>}
   */
  static fuseCache = new Map();

  constructor() {
    super();
    this.getOrBuildFuse(this.locale).catch(() => {});
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.locale = document.documentElement.getAttribute('lang') || 'en';
    this.cachedResults = {};
    this.allPredictiveSearchInstances = [this];

    this.setupEventListeners();
  }

  async getOrBuildFuse(locale) {
    const cached = PredictiveSearch.fuseCache.get(locale);
    if (cached) return cached;

    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}collections/all`, window.location.origin);
    url.searchParams.set('view', 'search-data.json');

    let dataset;
    const res = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Dataset fetch failed: ${res.status}`);
    try {
      dataset = await res.json();
    } catch {
      dataset = JSON.parse(await res.text());
    }

    const fuse = new Fuse(dataset, {
      includeScore: true,
      includeMatches: true,
      ignoreLocation: true,
      threshold: 0.35,
      minMatchCharLength: 2,
      keys: [
        { name: 'title',  weight: 0.5 },
        { name: 'vendor', weight: 0.2 },
        { name: 'type',   weight: 0.15 },
        { name: 'tags',   weight: 0.15 },
      ],
    });

    const entry = { fuse, dataset };
    PredictiveSearch.fuseCache.set(locale, entry);
    return entry;
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c =>
      ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    const term = (searchTerm ?? '').trim();
    if (!term) { this.renderSearchResults('', 0); return; }

    const key = `${this.locale}:${term.replace(/\s+/g, '-').toLowerCase()}`;
    this.setLiveRegionLoadingState();

    const cached = this.cachedResults[key];
    if (cached) {
      this.renderSearchResults(cached, this.cachedResults[key + ':count'] ?? 0);
      return;
    }

    try {
      const { fuse } = await this.getOrBuildFuse(this.locale);
      const hits = fuse.search(term, { limit: 8 });
      const suggestion = this.buildSuggestion(term, hits);
      const markup = this.buildShopifyPredictiveMarkup(hits, suggestion);
      const count  = hits.length + (suggestion ? 1 : 0);

      this.allPredictiveSearchInstances.forEach(inst => {
        inst.cachedResults[key] = markup;
        inst.cachedResults[key + ':count'] = count;
      });

      this.renderSearchResults(markup, count);
    } catch (err) {
      console.error(err);
      this.close();
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  tokenizeForSuggest(str) {
    return String(str || '')
      .toLowerCase()
      .split(/[^a-z0-9]+/i)
      .filter(Boolean);
  }

  buildSuggestion(term, hits) {
    const q = (term || '').toLowerCase();
    if (!q) return '';

    const freq = new Map();
    for (const h of hits.slice(0, 10)) {
      const it = h.item || {};
      const fields = [
        it.title, it.vendor, it.type,
        ...(Array.isArray(it.tags) ? it.tags : []),
      ].filter(Boolean);

      for (const f of fields) {
        const tokens = Array.isArray(f)
          ? f.flatMap(x => this.tokenizeForSuggest(x))
          : this.tokenizeForSuggest(f);

        for (const t of tokens) {
          if (t.startsWith(q) && t.length > q.length) {
            freq.set(t, (freq.get(t) || 0) + 1);
          }
        }
      }
    }

    if (!freq.size) return term;

    return [...freq.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].length - b[0].length || a[0].localeCompare(b[0]))[0][0];
  }

  buildShopifyPredictiveMarkup(hits, term) {
    const items = hits.map(h => h.item);
    const countProducts = items.length;
    const countSuggestions = term ? 1 : 0;
    const totalCountText = `${countProducts + countSuggestions} results: ${countSuggestions} suggestion${countSuggestions===1?'':'s'}, ${countProducts} product${countProducts===1?'':'s'}`;

    const suggestionsLabel = window.predictiveSearchStrings?.suggestionsLabel || 'Suggestions';
    const suggestionList = countSuggestions ? `
    <div class="predictive-search__result-group">
      <div>
        <h2 id="predictive-search-queries" class="predictive-search__heading text-body caption-with-letter-spacing">${this.escapeHtml(suggestionsLabel)}</h2>
        <ul id="predictive-search-results-queries-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-queries">
          <li id="predictive-search-option-query-1" class="predictive-search__list-item" role="option" aria-selected="false">
            <a href="/search?q=${encodeURIComponent(term)}&_pos=1&_psq=${encodeURIComponent(term)}&_ss=e&_v=1.0"
               class="predictive-search__item link link--text" tabindex="-1">
              <div class="predictive-search__item-content predictive-search__item-content--centered">
                <p class="predictive-search__item-heading predictive-search__item-query-result h5" aria-label="${this.escapeHtml(term)}">
                  <mark>${this.escapeHtml(term)}</mark>
                  <span></span>
                </p>
              </div>
            </a>
          </li>
        </ul>
      </div>
    </div>` : '';

    const productsList = items.map((p, idx) => {
      const img = p.image && p.image.startsWith('//') ? `https:${p.image}` : p.image;
      const id = `predictive-search-option-product-${idx+1}`;
      return `
      <li id="${id}" class="predictive-search__list-item" role="option" aria-selected="false">
        <a href="${p.url}?_pos=1&_psq=${encodeURIComponent(term)}&_ss=e&_v=1.0"
           class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
          ${img ? `<img class="predictive-search__image" src="${img}" alt="${this.escapeHtml(p.title)}" width="50" height="50">` : ''}
          <div class="predictive-search__item-content predictive-search__item-content--centered">
            <p class="predictive-search__item-heading h5">${p.title}</p>
          </div>
        </a>
      </li>`;
    }).join('');

    const productsGroup = `
    <div class="predictive-search__result-group">
      <div>
        <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">${this.locale.startsWith('ar') ? 'المنتجات' : 'Products'}</h2>
        <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
          ${productsList}
        </ul>
      </div>
    </div>`;

    const searchForButton = `
    <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
      <button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow"
              tabindex="-1" role="option" aria-selected="false"
              onclick="location.href='/search?q=${encodeURIComponent(term)}'">
        <span data-predictive-search-search-for-text="">${this.locale.startsWith('ar') ? 'ابحث عن' : 'Search for'} “${this.escapeHtml(term)}”</span>
        <span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-arrow" viewBox="0 0 14 10"><path fill="currentColor" fill-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546" clip-rule="evenodd"></path></svg></span>
      </button>
    </div>`;

    return `
    <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
      ${suggestionList}
      ${productsGroup}
    </div>
    <div class="predictive-search__loading-state">
      <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg>
    </div>
    ${searchForButton}
    <span class="hidden" data-predictive-search-live-region-count-value="">
      ${this.escapeHtml(totalCountText)}
    </span>`;
  }

  renderSearchResults(resultsMarkup, count = 0) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults(count);
    this.open();
  }

  setLiveRegionResults(count = 0) {
    this.removeAttribute('loading');
    const template = this.getAttribute('data-results-text') || '{count} results';
    const msg = template.replace('{count}', count);
    this.setLiveRegionText(msg);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
